import { useEffect, useMemo, useState } from 'react'
import { useCore, useLogin } from '../../hooks'
import { FilterConfig, useFilters } from './useFilters'
import { disposalFilters } from '../../gql/graphql'

export type DisposalSentFilter = 'all' | 'pending' | 'sent'

export function useDisposalFilters() {
  const { company } = useCore()

  const { user } = useLogin()
  const childCompanies = user.company.children

  // Local states managed through useFilters to get URL sync behavior
  const [carrierId, setCarrierId] = useState<string | undefined>(undefined)
  const [childCompanyId, setChildCompanyId] = useState<string | undefined>(undefined)
  const [sent, setSent] = useState<DisposalSentFilter>('all')

  // Default company when there are no child companies
  useEffect(() => {
    if ((childCompanies?.length || 0) === 0 && company?.id) {
      setChildCompanyId(String(company.id))
    }
  }, [childCompanies, company?.id])

  const filterConfig: FilterConfig = useMemo(
    () => ({
      carrierId: { type: 'string', coreState: carrierId, coreSetter: setCarrierId },
      childCompanyId: { type: 'string', coreState: childCompanyId, coreSetter: setChildCompanyId },
      sent: { type: 'string', coreState: sent, coreSetter: setSent, default: 'all' },
    }),
    [carrierId, childCompanyId, sent],
  )

  const { values, setFilter, resetFilters } = useFilters(filterConfig, '/dispatch')

  const selectedCarrierId: string | undefined = values.carrierId
  const selectedChildCompanyId: string | undefined = values.childCompanyId
  const selectedSent: DisposalSentFilter = values.sent || 'all'

  // Filters for GraphQL query - only include supported fields
  const apiFilters = useMemo(() => {
    return {
      carrier: selectedCarrierId || undefined,
      companies: selectedChildCompanyId ? [Number(selectedChildCompanyId)] : undefined,
    } as disposalFilters
  }, [selectedCarrierId, selectedChildCompanyId])

  const setSelectedCarrierId = (value?: string) => setFilter('carrierId', value)
  const setSelectedChildCompany = (value?: string) => setFilter('childCompanyId', value === 'all' ? undefined : value)
  const setSelectedSent = (value: DisposalSentFilter) => setFilter('sent', value)

  return {
    selectedCarrierId,
    setSelectedCarrierId,
    selectedChildCompanyId,
    setSelectedChildCompany,
    selectedSent,
    setSelectedSent,
    apiFilters,
    resetFilters,
  }
}
